import { faCube, faEye } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper, Table } from "@tanstack/react-table";
import { clamp } from "lodash";
import React from "react";
import { twJoin } from "tailwind-merge";

import { GenericObject } from "src/api/flowTypes";
import { ColumnFilter } from "src/base-components/ColumnFilter";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { Cell, ColumnVisibilityDropdown } from "src/base-components/Table";
import {
  EnumValue,
  EventConfigEnriched,
  PropertyDefinitionOutput,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { parseJsonOrReturnAsIs } from "src/datasets/DatasetTable/utils";
import { TruncatedIdPill } from "src/decisionsOverview/TruncatedIdPill";
import { Tooltip } from "src/design-system/Tooltip";
import { JsonContent } from "src/entities/entityView/EntityDetailsSidePane";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { Cardinality, EntitySchemaResource } from "src/entities/queries";
import {
  SensitiveField,
  SensitiveFieldValue,
} from "src/entitiesEventsCommon/SensitiveField";
import { RelationIcon } from "src/eventsCatalogue/SchemaEditor/RelationIcons";
import { DeserializedEvent } from "src/eventsCatalogue/queries";
import { Environment } from "src/hooks/useEnvironment";
import { TYPE_ICONS } from "src/utils/constants";
import { formatDate } from "src/utils/datetime";
import { formatNumber } from "src/utils/numbers";
import { speakPythonPrimitive } from "src/utils/speakPython";

const helper = createColumnHelper<DeserializedEvent>();

export const PropertyWithIcon = ({
  children,
  type,
}: {
  children: React.ReactNode;
  type: PropertyDefinitionOutputTypeEnum;
}) => {
  return (
    <Tooltip placement="top" title={children} asChild>
      <div className="flex items-center gap-0.5 truncate pl-1 pr-2 text-left text-gray-800 font-inter-medium-12px">
        {type === PropertyDefinitionOutputTypeEnum.RELATION ? (
          <RelationIcon cardinality={Cardinality.ONE} size="2xs" />
        ) : (
          <Icon
            color="text-gray-500"
            icon={TYPE_ICONS[type as keyof typeof TYPE_ICONS]}
            size="2xs"
          />
        )}
        <span className="truncate">{children}</span>
      </div>
    </Tooltip>
  );
};

const HeaderCell: React.FC<{
  filterKey: string;
  content: string;
  value: PropertyDefinitionOutput;
  filters?: Record<string, string | null>;
  onFilterChange?: (filters: Record<string, string | null>) => void;
}> = ({ filterKey: key, content, value, filters, onFilterChange }) => {
  return (
    <div className="flex h-8 items-center justify-between bg-white">
      <PropertyWithIcon type={value.type}>{content}</PropertyWithIcon>
      {onFilterChange &&
        value.type === PropertyDefinitionOutputTypeEnum.ENUM &&
        value.enum_schema && (
          <ColumnFilter
            description={`Filter by ${value.display_name}`}
            elements={value.enum_schema.values.map((v) => ({
              key: v.value,
              value: v.value,
            }))}
            isFiltering={!!filters?.[key]}
            selected={filters?.[key] ?? undefined}
            onResetRequest={() => onFilterChange({ ...filters, [key]: null })}
            onSelect={(filter) => onFilterChange({ ...filters, [key]: filter })}
          />
        )}
    </div>
  );
};

export const getColumns = (
  schema: EventConfigEnriched,
  environment: Environment,
  entitiesSchemasData?: {
    entities: EntitySchemaResource[];
    next_page_token: string;
  },
  filtering?: {
    filters: Record<string, string | null>;
    onFilterChange: (filters: Record<string, string | null>) => void;
  },
) => {
  const idColumnDisplayName = "ID";
  const timestampColumnDisplayName = "Timestamp";
  const baseColumns = [
    helper.accessor("payload.id", {
      header: () => (
        <HeaderCell
          content={idColumnDisplayName}
          filterKey="id"
          value={schema.properties.id}
        />
      ),
      cell: (info) => (
        <Cell
          className={twJoin(
            "group-[.selected]:bg-gray-50",
            "w-24 group-hover:bg-gray-50",
          )}
          info={info}
        >
          <TruncatedIdPill id={info.getValue()} />
        </Cell>
      ),
      maxSize: 90,
      enableHiding: false,
      meta: {
        displayName: idColumnDisplayName,
        sticky: "left",
      },
    }),
    helper.accessor("payload.timestamp", {
      header: () => (
        <HeaderCell
          content={timestampColumnDisplayName}
          filterKey="timestamp"
          value={schema.properties.timestamp}
        />
      ),
      cell: (info) => (
        <Cell info={info}>
          {formatDate(info.getValue(), "MMM d, yyyy 'at' h:mm a")}
        </Cell>
      ),
      enableHiding: false,
      maxSize: 200,
      meta: {
        displayName: timestampColumnDisplayName,
      },
    }),
  ];

  const remainingColumns = Object.entries(schema.properties)
    .filter(([key]) => !["id", "timestamp"].includes(key))
    .map(([key, value]: [string, PropertyDefinitionOutput]) => {
      return helper.accessor(`payload.${key}`, {
        header: () => (
          <HeaderCell
            content={value.display_name ?? key}
            filterKey={key}
            filters={
              value.type === PropertyDefinitionOutputTypeEnum.ENUM
                ? filtering?.filters
                : undefined
            }
            value={value}
            onFilterChange={
              value.type === PropertyDefinitionOutputTypeEnum.ENUM
                ? filtering?.onFilterChange
                : undefined
            }
          />
        ),
        cell: (info) => {
          const cellValue = info.getValue();

          if (
            value.type === PropertyDefinitionOutputTypeEnum.DATETIME ||
            value.type === PropertyDefinitionOutputTypeEnum.DATE
          ) {
            return (
              <Cell info={info}>
                {cellValue
                  ? formatDate(
                      cellValue as string,
                      getDateFormatForPropertyType(value.type),
                    )
                  : null}
              </Cell>
            );
          }

          if (value.type === PropertyDefinitionOutputTypeEnum.NUMBER) {
            if (!cellValue) return null;

            const digits = getMaxDigitsForNumberColumn(info.table, key);

            return (
              <Cell className="justify-end font-code-13" info={info}>
                {formatNumber(cellValue as number, {
                  maximumFractionDigits: digits,
                  minimumFractionDigits: digits,
                })}
              </Cell>
            );
          }

          if (value.type === PropertyDefinitionOutputTypeEnum.INTEGER) {
            if (!cellValue) return null;

            return (
              <Cell className="justify-end font-code-13" info={info}>
                {formatNumber(cellValue as number)}
              </Cell>
            );
          }

          if (value.type === PropertyDefinitionOutputTypeEnum.ENUM) {
            const parsedCellValue = parseJsonOrReturnAsIs(cellValue as string);
            const enumValue = value.enum_schema?.values.find(
              (v: EnumValue) => v.value === parsedCellValue,
            );
            if (!enumValue) return;

            return (
              <Cell info={info}>
                <EnumPill enumValue={enumValue} />
              </Cell>
            );
          }

          if (value.type === PropertyDefinitionOutputTypeEnum.RELATION) {
            if (!cellValue) return null;
            const relationSchema = value.relation_schema?.entity_type
              ? findSchema(
                  value.relation_schema.entity_type,
                  entitiesSchemasData,
                )
              : undefined;

            return (
              <Cell info={info}>
                <EntityDetailsWindowPill
                  entityIcon={
                    relationSchema ? getSchemaIcon(relationSchema) : faCube
                  }
                  environment={environment}
                  fullWidth={false}
                  id={(cellValue as DeserializedEvent["payload"])._id}
                  name={(cellValue as DeserializedEvent["payload"])._id}
                  schemaId={(cellValue as DeserializedEvent["payload"])._type}
                />
              </Cell>
            );
          }

          if (value.type === PropertyDefinitionOutputTypeEnum.BOOLEAN) {
            if (cellValue === null || cellValue === undefined) return null;

            return (
              <Cell info={info}>{speakPythonPrimitive(String(cellValue))}</Cell>
            );
          }

          if (value.type === PropertyDefinitionOutputTypeEnum.ADDRESS) {
            return (
              <Cell info={info}>
                {value._sensitive ? (
                  <SensitiveField
                    propertyName={value.display_name ?? key}
                    value={cellValue as SensitiveFieldValue}
                  />
                ) : (
                  <Tooltip
                    placement="top"
                    title={JSON.stringify(cellValue)}
                    asChild
                  >
                    <div className="truncate">
                      <JsonContent
                        content={cellValue as GenericObject}
                        title={value.display_name ?? key}
                      />
                    </div>
                  </Tooltip>
                )}
              </Cell>
            );
          }

          return (
            <Cell info={info}>
              {value._sensitive ? (
                <SensitiveField
                  propertyName={value.display_name ?? key}
                  value={cellValue as SensitiveFieldValue}
                />
              ) : (
                <Tooltip placement="top" title={cellValue as string} asChild>
                  <div className="truncate">{cellValue as string}</div>
                </Tooltip>
              )}
            </Cell>
          );
        },
        meta: {
          displayName: value.display_name ?? value.type,
        },
        maxSize: 300,
      });
    });

  return [
    ...baseColumns,
    ...remainingColumns,
    helper.display({
      id: "actions",
      header: (info) => (
        <div className="w-13.5 flex justify-end bg-white pr-1.5">
          <ColumnVisibilityDropdown min={3} table={info.table} />
        </div>
      ),
      cell: () => <EventActions />,
      minSize: 40,
      maxSize: 40,
      meta: {
        sticky: "right",
      },
    }),
  ];
};

const EventActions = () => {
  return (
    <div
      className={twJoin(
        "w-13.5 flex h-8 justify-end pr-1.5",
        "bg-white group-hover:bg-gray-50 group-[.selected]:bg-gray-50",
      )}
    >
      <Tooltip placement="top" title="View Details">
        <span className="invisible group-hover:visible group-[.selected]:visible">
          <Icon
            color="group-[.selected]:text-indigo-600 group-[:not(.selected)]:text-gray-500"
            dataLoc="event-view"
            icon={faEye}
            size="xs"
          />
        </span>
      </Tooltip>
    </div>
  );
};

export const EnumPill = ({ enumValue }: { enumValue: EnumValue }) => (
  <Pill size="sm" variant={enumValue.color || "gray"}>
    <Pill.Text>{enumValue.value}</Pill.Text>
  </Pill>
);

const getMaxDigitsForNumberColumn = (
  table: Table<DeserializedEvent>,
  columnId: string,
) => {
  const values = table
    .getRowModel()
    .rows.map((row) => row.original.payload[columnId])
    .filter((value): value is number => typeof value === "number");

  return values.reduce((min, value) => {
    const digits = value.toString().split(".")[1]?.length || 0;
    return clamp(digits, min, 4);
  }, 0);
};

const getDateFormatForPropertyType = (
  type: PropertyDefinitionOutputTypeEnum,
) => {
  if (type === PropertyDefinitionOutputTypeEnum.DATETIME) {
    return "MMM d, yyyy 'at' h:mm a";
  }

  return "MMM d, yyyy";
};
